interface TypographyConfig {
  headingFont?: string;
  bodyFont?: string;
  headingFontWeight?: number;
  bodyFontWeight?: number;
  primaryText?: string;
  secondaryText?: string;
  accentColor?: string;
  textCase?: "default" | "uppercase" | "lowercase";
}

/**
 * CSS variable declarations that belong on the scope root (`:root` or the
 * portal container).
 */
export function buildTypographyVarDeclarations(
  config: TypographyConfig,
): string[] {
  const declarations: string[] = [];

  const write = (value: string | undefined, targets: readonly string[]) => {
    if (!value) {
      return;
    }
    for (const target of targets) {
      declarations.push(`${target}: ${value};`);
    }
  };

  write(config.accentColor, [
    "--redo-primary-color",
    "--redo-primary-button-color",
  ]);
  write(config.headingFont, ["--return-app-title-font-family"]);
  write(config.bodyFont, ["--return-app-body-font-family"]);
  write(config.primaryText, ["--redo-colors-text-text-primary"]);
  // The builder exposes only two text-color knobs for primary and secondary;
  // `secondary` maps to secondary and tertiary colors so merchants aren't
  // managing three colors.
  write(config.secondaryText, [
    "--redo-colors-text-text-secondary",
    "--redo-colors-text-text-tertiary",
  ]);

  return declarations;
}

/** Selector-based typography rules (font-family, color, link color). */
export function buildTypographySelectorRules(
  s: string,
  root: string,
  config: TypographyConfig,
): string[] {
  // The body-font rule sets `font-family` on the scope root AND on the modal
  // scope so body-portaled modals (rendered at <body>, outside the
  // customer-portal subtree) still pick up the merchant font.
  const bodyFontRule = config.bodyFont
    ? `${root}, [data-ui="portal-modal"] { font-family: ${config.bodyFont}; }`
    : undefined;

  // Target `body` (not `:root`) in global mode so the rule beats the
  // user-agent `body { color: canvastext }` default. Also pin the merchant
  // primary text on `[data-ui="portal-modal"]` so MUI Paper's hardcoded
  // black `color` doesn't leak through.
  const primaryTextScope = s.trimEnd();
  const primaryTextRule = config.primaryText
    ? `${primaryTextScope || "body"}, [data-ui="portal-modal"] { color: ${config.primaryText}; }`
    : undefined;

  const headingFontRule = config.headingFont
    ? `${s}h1, ${s}h2, ${s}h3, ${s}[data-ui="header"], [data-ui="portal-modal"] h1, [data-ui="portal-modal"] h2, [data-ui="portal-modal"] h3, [data-ui="portal-modal"] [data-ui="header"] { font-family: ${config.headingFont} !important; }`
    : undefined;

  // text-transform: a universal selector applied to every descendant of each
  // portal-rooted scope. The universal `*` catches form controls (input,
  // textarea, select, button) and `::placeholder` — which don't inherit
  // text-transform — plus body-portaled popovers like
  // `[data-portal-selector="redo-dropdown"]` (country selects, single-select dropdowns) and
  // MUI modals at `[data-ui="portal-modal"]`. Specificity is intentionally
  // low so per-component styles can still override if needed.
  const textCaseRules: string[] = [];
  if (config.textCase === "uppercase" || config.textCase === "lowercase") {
    textCaseRules.push(
      `${s}*, [data-ui="portal-modal"] *, [data-portal-selector="redo-dropdown"] * { text-transform: ${config.textCase}; }`,
    );
  }

  // Uppercase visually appears larger than mixed case at the same font-size,
  // so when uppercase is active we shrink the design system's text-size
  // classes by 3px. These are stable global classes (defined in
  // redo/web/src/theme/typography.css) with fixed px values — every
  // `<Text>` / `<Header>` in the Redo design system renders with one of
  // them. Targeting them by name (rather than `*` or element types) keeps
  // the shrink off layout wrappers, so nothing compounds: each rule
  // overrides a specific fixed px value, and inherited sizes don't
  // re-shrink because they have no own font-size for the rule to match.
  if (config.textCase === "uppercase") {
    const textSizes: Record<string, number> = {
      "text-xxs": 10,
      "text-xs": 12,
      "text-sm": 14,
      "text-md": 16,
      "text-lg": 18,
      "text-xl": 20,
      "text-2xl": 24,
      "text-3xl": 30,
      "text-4xl": 36,
      "text-5xl": 48,
      "text-6xl": 60,
      "header-xxs": 18,
      "header-xs": 24,
      "header-sm": 30,
      "header-md": 36,
      "header-lg": 48,
      "header-xl": 60,
      "header-2xl": 72,
    };
    // Prefix every selector with the scope so the rule (a) only applies
    // inside the portal / builder preview (`[data-portal-builder-preview]`
    // in the builder, empty global in the live portal), and (b) outranks
    // the bare-class rule in redo/web/src/theme/typography.css on
    // specificity. Also emit a copy inside `[data-ui="portal-modal"]` and
    // `[data-portal-selector="redo-dropdown"]` so body-portaled surfaces (cart drawer, MUI
    // modals, country dropdown popovers) shrink too.
    const scopes = [
      s,
      '[data-ui="portal-modal"] ',
      '[data-portal-selector="redo-dropdown"] ',
    ];
    for (const [className, px] of Object.entries(textSizes)) {
      // Clamp to a sane minimum so `text-xxs` (10px) doesn't disappear.
      const shrunk = Math.max(8, px - 3);
      const selector = scopes
        .map((scopePrefix) => `${scopePrefix}.${className}`)
        .join(", ");
      textCaseRules.push(`${selector} { font-size: ${shrunk}px; }`);
    }
  }

  // Font-weight rules use the same scoping as the font-family rules above.
  // `!important` is required because `.weight-semibold`/`.weight-bold` set
  // font-weight directly on elements at single-class specificity, which would
  // otherwise tie or beat our scope-root rule.
  const bodyFontWeightRule = config.bodyFontWeight
    ? `${root}, [data-ui="portal-modal"] { font-weight: ${config.bodyFontWeight} !important; }`
    : undefined;

  const headingFontWeightRule = config.headingFontWeight
    ? `${s}h1, ${s}h2, ${s}h3, ${s}[data-ui="header"], [data-ui="portal-modal"] h1, [data-ui="portal-modal"] h2, [data-ui="portal-modal"] h3, [data-ui="portal-modal"] [data-ui="header"] { font-weight: ${config.headingFontWeight} !important; }`
    : undefined;

  return [
    bodyFontRule,
    primaryTextRule,
    headingFontRule,
    ...textCaseRules,
    bodyFontWeightRule,
    headingFontWeightRule,
  ].filter((rule): rule is string => rule !== undefined);
}
